import { Component, ViewChild } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { VerticalFilterItemCreationCallback } from "@app/directives/_resourceFilters/vertical-filter-list/vertical-filter-list.component";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IUser } from "@app/interfaces/IUser";
import { IAccount } from "@app/services/account.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { IEngineerJob, IEngineerJobItem } from "../../foundation.interfaces";
import { VisitFailureReason } from "../../_enums/VisitFailureReason";
import { IVisitFailureRecord } from "../../_models/IVisitFailureRecord";
import { IVisitFailureRecordQueryOptions } from "../../_models/IVisitFailureRecordQueryOptions";
import { AccountService } from "../../_services/account.service";
import { EngineerJobItemService } from "../../_services/engineer-job-item.service";
import { EngineerJobService } from "../../_services/engineer-job.service";
import { UserService } from "../../_services/user.service";
import { VisitFailureService } from "../../_services/visit-failure.service";
import { VisitFailureFormComponent } from "../visit-failure-form/visit-failure-form.component";
import './visit-failures.component.scss';

@UntilDestroy()
@Component({
    selector: 'visit-failures',
    template: require('./visit-failures.component.html')
})
export class VisitFailuresComponent {
    loading: boolean;
    loadingJob: boolean;
    loadingJobItems: boolean;

    startDate: Date;
    endDate: Date;

    users: Map<number, IUser>;
    accounts: IAccount[];
    accountsMap: Map<number, IAccount>;
    selectedAccount: IAccount;
    jobItems: IEngineerJobItem[];
    jobItemsMap: Map<number, IEngineerJobItem>;
    accountJobItems: IEngineerJobItem[];
    selectedJobItem: IEngineerJobItem;
    selectedJob: IEngineerJob;
    accountJobs: IEngineerJob[];

    visitFailureRecords: IVisitFailureRecord[];
    selectedVisitFailureRecord: IVisitFailureRecord;

    tableColumns: IVTableColumn<IVisitFailureRecord>[];
    tableRows: IVTableRow[];
    filterTabs: IFilterBarTab<IVisitFailureRecord>[];

    showNewRecordForm: boolean;

    VisitFailureReason = VisitFailureReason;

    @ViewChild('visitFailureForm') visitFailureForm: VisitFailureFormComponent;

    constructor(
        private readonly accountService: AccountService,
        private readonly dateService: DateService,
        private readonly engineerJobItemService: EngineerJobItemService,
        private readonly engineerJobService: EngineerJobService,
        private readonly filterBarService: FilterBarService,
        private readonly translationService: TranslationService,
        private readonly userService: UserService,
        private readonly visitFailureService: VisitFailureService,
        private readonly vTableService: VTableService
    ) { }

    ngOnInit(): void {
        this.startDate = this.dateService.addOrSubtractTime(new Date(), -7, DateUnit.Day);
        this.endDate = new Date();

        this.createTable();
        this.createTabs();

        this.getData();
    }
    
    onFilterBarChanged(filterEvent: IAggregateFilterEvent<IVisitFailureRecord>): void {
        if (filterEvent.trigger == "date") {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.getData();
        }
    }

    filterRecordByAccount(record: IVisitFailureRecord, account: IAccount): boolean {
        return record.AccountId === account.Id;
    }

    accountSearchPredicate(account: IAccount, searchText: string): boolean {
        return account.CompanyName.toLowerCase().indexOf(searchText) >= 0;
    }

    onAccountClicked(account: IAccount): void {
        this.selectedAccount = account;

        this.loadingJob = true;

        this.engineerJobService.getEngineerJobsForAccount(account.Id).pipe(untilDestroyed(this)).subscribe({
            next: jobs => {
                this.accountJobs = jobs;
                this.loadingJob = false;
            }
        })
    }

    onJobClicked(job: IEngineerJob): void {
        this.selectedJob = job;

        this.accountJobItems = this.jobItems.filter(jobItem => jobItem.JobId === job.Id);
    }

    onJobItemClicked(jobItem: IEngineerJobItem): void {
        this.selectedJobItem = jobItem;
    }

    createNewRecord(): void {
        this.showNewRecordForm = true;
    }

    closeNewRecordForm(newRecord?: IVisitFailureRecord): void {
        this.showNewRecordForm = false;
        this.clearAccountAndJobs();

        if (newRecord) {
            this.visitFailureRecords.push(newRecord);
            this.createTableRows();
        }
    }

    jobItemSearchPredicate(jobItem: IEngineerJobItem, text: string): boolean {
        return jobItem.Id?.toString().toLowerCase().includes(text.toLowerCase()) || jobItem.Comments?.toString().toLowerCase().includes(text.toLowerCase())
    }

    jobSearchPredicate(job: IEngineerJob, text: string): boolean {
        return job.Id?.toString().toLowerCase().includes(text.toLowerCase()) || job.Description?.toString().toLowerCase().includes(text.toLowerCase());
    }

    clearAccountAndJobs(): void {
        this.selectedAccount =
            this.accountJobs =
            this.accountJobItems =
            this.selectedJob =
            this.selectedJobItem =
            null;
    }

    clearSelectedJobItems(): void {
        this.accountJobItems = null;
        this.selectedJobItem = null;
    }
    
    onRowClicked(row: IVTableRow): void {
        this.selectedVisitFailureRecord = row.data;

        this.loadingJob = true;

        this.engineerJobItemService.getEngineerJobItems({ jobItemId: this.selectedVisitFailureRecord.JobItemId }).pipe(
            untilDestroyed(this),
            mergeMap(jobItems => {
                this.selectedJobItem = jobItems[0];
                return this.engineerJobService.getEngineerJob(this.selectedJobItem.JobId)
            })).subscribe({
                next: job => {
                    this.selectedJob = job;
                    this.loadingJob = false;
                }
            })
    }

    closeSelectedVisitFailRecord(updatedRecord?: IVisitFailureRecord): void {
        this.selectedVisitFailureRecord = null;
        this.clearAccountAndJobs();

        if (updatedRecord) {
            this.visitFailureRecords = this.visitFailureRecords.filter(record => record.Id !== updatedRecord.Id);
            this.visitFailureRecords.push(updatedRecord);
            this.createTableRows();
        }
    }

    reasonItemCreationCallback: VerticalFilterItemCreationCallback<IVisitFailureRecord> = (record) => {

        return {
            name: this.translationService.tEnum(VisitFailureReason, "VisitFailureReason", record.VisitFailureReason),
            data: record,
            predicate: (d) => d.VisitFailureReason === record.VisitFailureReason
        };
    }

    //--

    private getData(): void {
        this.loading = true;

        const options: IVisitFailureRecordQueryOptions = {
            Start: this.startDate,
            End: this.endDate
        }

        combineLatest([
            this.visitFailureService.getVisitFailures(options),
            this.userService.getAllUsers(),
            this.accountService.getCustomerAccounts(),
            this.engineerJobItemService.getAllEngineerJobItems()
        ]).pipe(untilDestroyed(this)).subscribe({
            next: ([records, users, accounts, jobItems]) => {
                this.visitFailureRecords = records;
                this.users = new Map(users.map(user => [user.Id, user]));

                this.accounts = accounts;
                this.accountsMap = new Map(accounts.map(account => [account.Id, account]));

                this.jobItems = jobItems;
                this.jobItemsMap = new Map(jobItems.map(jobItem => [jobItem.Id, jobItem]));


                if (!this.accountsMap.size || !this.users.size) {
                    return;
                }

                this.createTableRows();
                this.loading = false;
            }
        })
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.visitFailureRecords, this.tableColumns);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: record => this.vTableService.createDateCell(record.FailureTimestampUTC)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Account" }),
                createCellFunction: record => this.vTableService.createTableCell(this.getCustomerAccountName(record))
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: record => this.vTableService.createVehicleRegCell(undefined, record.VehicleId)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitFailures.JobId", sortType: SortType.Number }),
                createCellFunction: record => this.vTableService.createTableCell(this.getJobId(record))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitFailures.Username" }),
                createCellFunction: record => this.vTableService.createTableCell(this.getUsername(record))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "VisitFailures.EngineerName" }),
                createCellFunction: record => this.vTableService.createTableCell(this.getEngineerName(record))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Reason" }),
                createCellFunction: record => this.vTableService.createEnumCell(VisitFailureReason, "VisitFailureReason", record.VisitFailureReason)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Chargeable" }),
                createCellFunction: record => {
                    const isChargeable: string = record.Chargeable ?
                        this.translationService.t("Common.Yes") :
                        this.translationService.t("Common.No");

                    return this.vTableService.createTableCell(isChargeable);
                }
            }
        ]
    }

    private getCustomerAccountName(record: IVisitFailureRecord): string {
        const account = this.accountsMap.get(record.AccountId);
        return account.CompanyName;
    }


    private getEngineerName(record: IVisitFailureRecord): string {
        const engineer = this.users.get(record.EngineerId);
        return engineer.Description;
    }


    getUsername(record: IVisitFailureRecord): string {
        const user = this.users.get(record.UserId);
        return user.Description || user.Email;
    }

    private getJobId(record: IVisitFailureRecord): number {
        if (!record?.JobItemId) {
            return;
        }

        const jobItem = this.jobItemsMap.get(record.JobItemId);

        if (!jobItem) {
            return;
        }

        record.jobId = jobItem?.JobId;

        return jobItem?.JobId;
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IVisitFailureRecord>("Common.All", (record) => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IVisitFailureRecord>("Common.Chargeable", (record) => record.Chargeable));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IVisitFailureRecord>("Commissions.NotChargeable", (record) => !record.Chargeable));
    }
}
